import { RichText, Text, isEditorActive } from '@sitecore-jss/sitecore-jss-react';
import React, { useEffect, useRef } from 'react';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import useEEActive from '../../hooks/useEEActive';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import { targetLinkdsk } from '../../utils/targetDskLink';
import { CustomText } from '../@shared/customText';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import Picture from '../@shared/picture';
import SocialMediaBar from '../@shared/socialMediaBar';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import { ProductSpecialOffersDetailHeroBannerModel } from './models/ProductSpecialOffersDetailHeroBanner.model';
import { ProductSpecialOffersDetailHeroBannerProps } from './types/ProductSpecialOffersDetailHeroBanner.types';
import { appendRootToInternalLink } from '../../utils/appendRootToInternalLink.utils';
import useSitecoreContext from '../../hooks/useSitecoreContext';
import useExperiencerEditorPlaceholder from '../../hooks/useExperiencerEditorPlaceholder';
import CTAButton, { ctaEnums } from '../@shared/ctaButton';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import RoutableNotEditableLink from '../@shared/routableNotEditableLink';
import './styles.scss';

const ProductSpecialOffersDetailHeroBanner = (props: ProductSpecialOffersDetailHeroBannerProps) => {
  const isEE = useEEActive();
  const context = useSitecoreContext();
  const model = new ProductSpecialOffersDetailHeroBannerModel(props, context?.sitecoreContext);
  const smallHeroRef = useRef<HTMLDivElement | null>(null);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx.md})`);
  const apiResponse = isEmptyResponse(props);
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);
  const primaryCTAHref = appendRootToInternalLink(model.primaryLink?.value?.href);
  const secondaryCTAHref = appendRootToInternalLink(model.secondaryLink?.value?.href);

  const shouldShowDefaultImage = !model?.desktopImage?.url && !model?.mobileImage?.url;

  const productImage = {
    ...model.productImageProps,
    pictureClassName: 'special-hero-banner__image-wrapper',
    imageClassName: 'special-hero-banner__image',
  };

  if (shouldShowDefaultImage && (model?.defaultDesktopImage || model?.defaultMobileImage)) {
    productImage['mobileRendition'] = { url: model?.defaultMobileImage };
    productImage['desktopRendition'] = { url: model?.defaultDesktopImage };
  }

  useEffect(() => {
    if (isEE) return;
    if (smallHeroRef) {
      const smallHeroContentEl = smallHeroRef?.current?.querySelector('.special-hero-banner__content') as HTMLDivElement;
      if (!smallHeroContentEl) return;
      smallHeroContentEl.style.minHeight = `${
        isDesktop ? model?.desktopImage?.height : (model.mobileImage?.height || model?.desktopImage?.height) ?? 0
      }px`;
    }
  }, [smallHeroRef, isEE, isDesktop]);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }
  // star  node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }
  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }
  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  return (
      <div className="special-hero-banner" ref={smallHeroRef}>
        {model.socialMediaBarProps && (
          <SocialMediaBar {...model.socialMediaBarProps} extraClassName="special-hero-banner__social-media-bar" />
        )}
        <Picture productImageProps={productImage} />
        <div
          className={`special-hero-banner__content ${
            model.isGradientDisabled
              ? ''
              : 'special-hero-banner__content--background special-hero-banner__content--align-top special-hero-banner__content--top-margin'
          }`}
        >
          <Text field={{ value: model?.eyebrow || '' }} className="special-hero-banner__eyebrow" tag="p" />

          <CustomText tag={model.headingTag} field={{ value: model?.title || '' }} className={'special-hero-banner__title'} />

          <RichText
            field={{ value: model?.description || '' }}
            className="rich-text color-inherit-link special-hero-banner__description"
            tag="div"
          />

          <div className="special-hero-banner__button-wrapper">
            {model?.primaryLink && (
              <CTAButton type={ctaEnums.primaryRed}>
                <RoutableNotEditableLink
                  className="special-hero-banner__link special-hero-banner__link--primary"
                  href={primaryCTAHref}
                  target={targetLinkdsk(model.primaryLink, isDesktop)}
                >
                  {model.primaryLink.value.title}
                </RoutableNotEditableLink>
              </CTAButton>
            )}
            {model.secondaryLink && (
              <CTAButton type={ctaEnums.primaryWhite}>
                <RoutableNotEditableLink
                  className="special-hero-banner__link special-hero-banner__link--secondary"
                  href={secondaryCTAHref}
                  target={targetLinkdsk(model.secondaryLink, isDesktop)}
                >
                  {model.secondaryLink.value.title}
                </RoutableNotEditableLink>
              </CTAButton>
            )}
          </div>
        </div>
      </div>
  );
};

export default ProductSpecialOffersDetailHeroBanner;
